import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { navigate, useLocation, useParams } from '@reach/router'
import { navigateToExpiredLink } from '_utils/expired-link-navigation'

import { Loader } from '@refera/ui-web'
import { parse } from 'query-string'

import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  getButtonsAction,
  getServiceOrder,
  setCompartmentsList,
} from '_/modules/service-orders/actions'
import { getBudgetById, getStepStatusLog } from '_/modules/budget/actions'
import { getButtonsActionSelector } from '_/modules/service-orders/selectors'
import { getBudgetByIdSelectorJS, getStepStatusLogSelector } from '_/modules/budget/selectors'
import { classificationsServiceOrderSelector } from '_/modules/classification/selectors'
import { getClassification } from '_/modules/classification/actions'

import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'

import { Header } from '../Header'
import { ValuesAccordion, AttachmentsAccordion, ImagesAccordion } from '../Accordions'
import { InfoBar } from '_views/budget-view-v2/components/Header/info-bar'
import { useBudgetForm } from '_views/budget-view-v2/hooks/use-budget-form'
import { budgetFormSchema } from '../../utils/schemas'
import { budgetFormDefaultValues, SAAS_BUDGET_TYPES } from '_views/budget-view-v2/utils/constants'
import { formatBudgetResolutionsForGrid } from '_views/budget-view-v2/utils/functions'
import { normalizeSimplifiedResolutionsForWebForm } from '../../utils/normalizeSimplifiedResolutions'
import useStyles from './styles'

export function ViewContent() {
  const styles = useStyles()
  const { pathname, search } = useLocation()
  const { serviceOrderId, budgetId } = useParams()
  const { tipo: budgetType } = parse(search)
  const { loading, setLoading, setBudget, budget, resetForm, setResetForm } = useBudgetForm()

  const budgetSelector = useSelector(getBudgetByIdSelectorJS(budgetId))
  const stepStatusLog = useSelector(getStepStatusLogSelector)
  const classifications = useSelector(classificationsServiceOrderSelector) ?? []
  const buttonsActions = useSelector(getButtonsActionSelector)

  const [firstLoad, setFirstLoad] = useState(true)

  const dispatch = useDispatch()

  const { isTradesman, isSAAS, isAdmin } = useRolePermission()

  const { showToast } = useToast()

  const isCreating = pathname.includes('/novo')

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    resolver: yupResolver(budgetFormSchema),
    defaultValues: budgetFormDefaultValues(budgetSelector),
  })

  const { reset } = methods

  const handleCompartmentValues = useCallback(
    resolutions => {
      if (resolutions?.length) {
        const compartments = []
        resolutions.forEach(resolution => {
          if (resolution?.compartment) {
            compartments.push({ label: resolution?.compartment })
          }
        })

        if (compartments?.length) {
          dispatch(setCompartmentsList(compartments))
        }
      }
    },
    [dispatch]
  )

  const handleInitialValues = useCallback(() => {
    setBudget(budgetSelector)

    const formattedResolutions = normalizeSimplifiedResolutionsForWebForm(
      formatBudgetResolutionsForGrid(budgetSelector?.budgetResolution),
      budgetSelector
    )

    handleCompartmentValues(formattedResolutions)

    reset(
      {
        ...budgetFormDefaultValues(budgetSelector),
        visitHappenedTemp: budgetSelector?.visitHappenedTemp,
        budgetResolutions: formattedResolutions,
        budgetResolutionIdsToKeep: formattedResolutions?.map(i => i.id).join(','),
        isSimplified: true,
        budgetPictures: budgetSelector?.budgetPictures || [],
        budgetPicturesToDelete: [],
      },
      { keepDirty: false }
    )

    setResetForm(false)
    initialized.current = true
  }, [budgetSelector, reset])

  const initialized = useRef(false)

  const verifyProposeOpenedWithBudgetType = () => {
    if (!isSAAS || !budgetSelector) return
    if (
      isCreating &&
      ((budgetSelector?.proposeOpened && budgetType !== 'interno') ||
        (budgetSelector?.proposeOpened === false && budgetType !== 'externo'))
    ) {
      navigateToExpiredLink()
    }
  }

  const onPageLoad = useCallback(async () => {
    setLoading(true)
    setFirstLoad(true)
    try {
      const parsedServiceOrderId = parseInt(serviceOrderId, 10)

      // check if serviceOrderId is a valid number
      if (isNaN(parsedServiceOrderId)) {
        throw new Error(`Invalid serviceOrderId ${serviceOrderId}`, {
          cause: 'invalid_serviceorder_id',
        })
      }

      const isValidSAASBudgetType = SAAS_BUDGET_TYPES.includes(budgetType)

      if (
        isCreating &&
        ((!isTradesman && !isSAAS && !isAdmin) || (isSAAS && !isValidSAASBudgetType))
      ) {
        showToast({
          message: 'Você não tem permissão para esta ação.',
          type: 'error',
        })
        navigateToExpiredLink()
        return
      }

      // check if creating or viewing/editing
      if (!isCreating && isNaN(parseInt(budgetId, 10))) {
        throw new Error(`Invalid budgetId ${budgetId}`, {
          cause: 'invalid_budget_id',
        })
      }

      const currentServiceOrder = await dispatch(getServiceOrder(serviceOrderId))
      await dispatch(getStepStatusLog(serviceOrderId, undefined))

      if (isCreating) {
        if (currentServiceOrder.activeBudget) {
          showToast({
            message:
              'Este chamado possui um Orçamento ativo ou finalizado. Você deve recusar todos os orçamentos de tal chamado para executar esta ação.',
            type: 'error',
          })
          navigate('/')
          return
        }
      }

      // if budgetId is available, fetch it
      if (budgetId) {
        const params = { budgetView: true, showOriginalBudget: false }
        await dispatch(getBudgetById(budgetId, params))
      }
    } catch (error) {
      navigateToExpiredLink()
      return
    }

    if (!buttonsActions?.isEmpty()) {
      dispatch(getButtonsAction())
    }

    if (!classifications?.length) {
      dispatch(getClassification({ active: 'True', serviceOrder: serviceOrderId }))
    }

    setLoading(false)
    setFirstLoad(false)
  }, [
    loading,
    firstLoad,
    buttonsActions,
    stepStatusLog,
    serviceOrderId,
    budgetId,
    classifications,
    isTradesman,
    isSAAS,
  ])

  useEffect(() => {
    onPageLoad()
  }, [])

  useEffect(() => {
    if (!budget) {
      verifyProposeOpenedWithBudgetType()
      setBudget(budgetSelector)
    }
  }, [budgetSelector, dispatch])

  useEffect(() => {
    if ((budgetId && !initialized.current && budgetSelector) || resetForm) handleInitialValues()
  }, [resetForm, budgetSelector])

  if (firstLoad) {
    return <Loader hasBackdrop open />
  }

  return (
    <FormProvider {...methods}>
      <form id="simplified-budget-form">
        <Header />
        <InfoBar />
        <div className={styles.container}>
          <ImagesAccordion />
          <AttachmentsAccordion />
          <ValuesAccordion />
        </div>
      </form>
    </FormProvider>
  )
}
